import React, {useState} from 'react';
import {Box, Card, CardActions, CardContent, CardMedia, makeStyles, TextField, Typography} from '@material-ui/core';
import {PrimaryButton, SecondaryButton} from 'Components/Button';
import {Skeleton} from '@material-ui/lab';
import useIsMounted from "Hooks/useIsMounted";
import usePromotions from "Hooks/api/usePromotions";
import {IS_LOGGED_IN, IS_STAGING, SHARED_CDN_PATH, USE_NEW_CDN} from "Setup/config";
import BonusMoneyRulesStrapline from "Components/BonusMoneyRulesStrapline";
import {BP_MEDIUM} from "Theme/breakpoints";
import {sendGA4Event} from "Helpers/gaEvents";
import WebpImage from "Components/WebpImage";

const usePromotionCardStyles = makeStyles(theme => {
    const PromotionCardSettings = theme.overrides.PromotionCard;

    return {
        root: {
            border: PromotionCardSettings.border,
            borderRadius: PromotionCardSettings.borderRadius,
            background: PromotionCardSettings.background,
            backdropFilter: PromotionCardSettings.backdropFilter,
            color: PromotionCardSettings.contrastText,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 440,
            height: '100%',
            maxWidth: 320,
            width: '100%',
            margin: '0 auto',
            transition: 'all .2s ease',
            transform: 'translateY(2px)',
            boxShadow: '0 -1px 1px 0 rgb(0 0 0 / 5%), 0 1px 3px 0 rgb(0 0 0 / 20%)',
            '& img': {
                height: 230,
            },
            '&:hover': {
                transform: 'translateY(0px)',
                boxShadow: '0 4px 15px 0 rgb(0 0 0 / 30%), 0 0 1px 0 rgb(0 0 0 / 25%)'
            },
            '& .MuiTypography-h6': {
                lineHeight: '1.2',
                display: '-webkit-box',
                overflow: 'hidden',
                "-webkit-line-clamp": 2,
                "-webkit-box-orient": "vertical",

            },
            '& .MuiTypography-paragraph': {
                marginBottom: 0,
                display: '-webkit-box',
                overflow: 'hidden',
                "-webkit-line-clamp": 4,
                "-webkit-box-orient": "vertical"
            },
            '& .bg--bonus-money-strapline': {
                color: PromotionCardSettings.contrastText,
                opacity: 0.6,
                paddingBottom: theme.spacing(1)
            }
        },
        cardAction: {
            ...PromotionCardSettings.cardAction,
            marginTop: 'auto',
            padding: theme.spacing(0, 0, 1.5, 0),
            '& button': {
                width: '150px'
            }
        },
        cardInfo: {
            padding: theme.spacing(1.5),
            '&.MuiCardContent-root:last-child': {
                paddingBottom: 0
            }
        },
        cardActionArea: {
            '&:hover': {
                background: 'transparent'
            }
        },
        cardImage: {
            objectFit: "cover",
            width: "100%"
        },
        description: {
            fontSize: 12
        },
        promoRibbons: {
            position: "absolute",
            top: theme.spacing(2),
        },
        promoRibbon: {
            padding: theme.spacing(1),
            background: PromotionCardSettings.background,
            color: PromotionCardSettings.contrastText,
            minWidth: 100,
            marginBottom: theme.spacing(1),
        },
        promoCodeContent: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            padding: theme.spacing(2)
        },
        promoCodeResult: {
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            flex: 1,
            textAlign: "center",

            '& > div': {
                marginTop: "initial"
            }
        },
        promoCodeInput: {
            margin: "auto",

            '& input': {
                color: theme.palette.primary.main,
                fontWeight: "bold"
            },

            '& .MuiOutlinedInput-input + fieldset, & .MuiOutlinedInput-root:hover fieldset': {
                borderColor: theme.palette.primary.main
            }
        },
        strapline: {
            [theme.breakpoints.up(BP_MEDIUM)]: {
                '& .column': {
                    padding: 0
                }
            },

        }
    }
});

const PromotionCard = ({status = false, showBMRstrapline = true, ...props}) => {
    const classes = usePromotionCardStyles();
    const {image, title, description, bonusType, skeleton, openModal} = props;

    const handleReadMore = () => {
        sendGA4Event('view_promotion', {
            promotion_id: props.promoId
        });

        openModal();
    };

    if (skeleton) {
        return (
            <Box maxWidth="320px" width="100%" mx="auto">
                <Skeleton variant="rect" width="100%" height="300px" animation={'wave'}/>
                <Skeleton variant="text" width="50%" animation={'wave'}/>
                <Skeleton variant="text" width="25%" animation={'wave'}/>
                <Box mx="auto" pt={3} pb={2} width="45%">
                    <Skeleton variant="rect" width="100%" height="40px" animation={'wave'}/>
                </Box>
                <Skeleton variant="text" width="100%" animation={'wave'}/>
                <Skeleton variant="text" width="100%" animation={'wave'}/>
            </Box>
        )
    }

    const fallbackImage = "https://via.placeholder.com/320x240/115F65";
    const imagePath = image.url ? image.url : fallbackImage;

    return (
        <Card className={classes.root}>
            {/* TODO: Remove this condition after fully switched to new CDN */}
            {USE_NEW_CDN ?
                <WebpImage className={classes.cardImage} ignoreWebp={!imagePath.includes(SHARED_CDN_PATH)} src={imagePath} alt={image.alt ?? ""}/> :
                <CardMedia
                    component={"img"}
                    title={title}
                    alt={image.alt ?? ""}
                    image={image.url ? image.url : fallbackImage}
                />
            }
            <CardContent className={classes.cardInfo}>
                <Typography gutterBottom component="h3" variant="h6">{title}</Typography>
                {description &&
                    <Typography className={classes.description} dangerouslySetInnerHTML={{__html: description}} paragraph/>
                }
            </CardContent>
            <CardActions className={classes.cardAction}>
                <SecondaryButton variant="outlined" onClick={handleReadMore}>Read More</SecondaryButton>
            </CardActions>
            {showBMRstrapline &&
                <BonusMoneyRulesStrapline transparent className={classes.strapline}/>
            }
        </Card>
    )
};

export const PromoCodeCard = () => {
    const classes = usePromotionCardStyles();

    const isMounted = useIsMounted();
    const {_optIn} = usePromotions();
    const {optInPromotion} = _optIn();
    const [promoCode, setPromoCode] = useState("");
    const [isSuccess, setIsSuccess] = useState(null);
    const [isOptingIn, setIsOptingIn] = useState(false);

    const handleOptInPromotion = async () => {
        setIsOptingIn(true);
        const res = await optInPromotion(promoCode);

        if (isMounted()) {
            setIsSuccess(res);
            setIsOptingIn(false);
        }
    };

    const handlePromoCodeChange = e => {
        const {value} = e.target;
        setPromoCode(value);
    };

    const reset = () => {
        setIsSuccess(null);
        setPromoCode("");
    };

    if (!IS_LOGGED_IN || !IS_STAGING) return null;

    return (
        <Card className={classes.root}>
            {isSuccess !== null ?
                <div className={classes.promoCodeResult}>
                    <CardContent className={classes.promoCodeContent}>
                        <Typography component="h3" variant="h6">{isSuccess ? 'Success!' : 'Not Found!'}</Typography>
                        <Typography className={classes.description} gutterBottom>
                            {isSuccess ?
                                'You successfully redeemed your bonus!' :
                                'The promo code you entered was invalid. Please try again.'
                            }
                        </Typography>
                    </CardContent>
                    <CardActions className={classes.cardAction}>
                        <PrimaryButton onClick={reset}>{isSuccess ? 'Enter Another' : 'Try Again'}</PrimaryButton>
                    </CardActions>
                </div> :
                <>
                    <CardContent className={classes.promoCodeContent}>
                        <Typography component="h3" variant="h6">Enter Promotional Code</Typography>
                        <Typography className={classes.description} gutterBottom>
                            Here you can enter a promotional code which can be used for free spins, cashback and instant bonuses!
                        </Typography>
                        <Typography className={classes.description}>Need to know:</Typography>
                        <ul>
                            <li>
                                <Typography className={classes.description} gutterBottom>
                                    Use for any bonuses except card registration bonuses
                                </Typography>
                            </li>
                            <li>
                                <Typography className={classes.description} gutterBottom>
                                    You must be eligible for a bonus in order to claim it
                                </Typography>
                            </li>
                        </ul>
                        <TextField InputLabelProps={{focused: true}} className={classes.promoCodeInput} onChange={handlePromoCodeChange} label="Promo Code"
                                   variant="outlined"/>

                        <BonusMoneyRulesStrapline column={false} transparent textStyle={{color: "#bababa"}}/>
                    </CardContent>

                    <CardActions className={classes.cardAction}>
                        <PrimaryButton isLoading={isOptingIn} onClick={handleOptInPromotion} disabled={promoCode.length < 1}>Submit</PrimaryButton>
                    </CardActions>
                </>
            }
        </Card>
    )
};

export default PromotionCard;
